import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useDemoMode } from "@/pages/demo/DemoModeContext";
import type { ItemBeneficiary } from "@/shared/hooks/useItemBeneficiaries";

interface AddBeneficiaryParams {
  estate_item_id: number;
  contact_id: number;
  share_percentage?: number | null;
}

interface UpdateBeneficiaryParams {
  id: number;
  estate_item_id: number;
  share_percentage?: number | null;
}

interface RemoveBeneficiaryParams {
  id: number;
  estate_item_id: number;
}

export function useUpdateBeneficiaries() {
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  const invalidate = (estateItemId: number) => {
    queryClient.invalidateQueries({ queryKey: ["item-beneficiaries", estateItemId] });
  };

  const addBeneficiary = useMutation({
    mutationFn: async (params: AddBeneficiaryParams) => {
      if (isDemoMode) {
        const entries = queryClient.getQueriesData<
          Array<{
            id: number;
            first_name: string | null;
            last_name: string | null;
            relationship: string | null;
          }>
        >({ queryKey: ["lawyer-person-contacts"] });
        const contact = entries.flatMap(([, d]) => d ?? []).find((c) => c.id === params.contact_id);
        const contactName = contact
          ? `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim() || "Contact"
          : "Contact";
        queryClient.setQueryData<ItemBeneficiary[]>(
          ["item-beneficiaries", params.estate_item_id],
          (prev = []) => [
            ...prev,
            {
              id: Date.now(),
              contact_id: params.contact_id,
              contact_name: contactName,
              relationship: contact?.relationship ?? null,
              share_percentage: params.share_percentage ?? null,
              numerator: null,
              denominator: null,
              is_contingent: false,
            },
          ],
        );
        return;
      }
      const { error } = await supabase.from("estate_item_beneficiaries").insert({
        estate_item_id: params.estate_item_id,
        contact_id: params.contact_id,
        share_percentage: params.share_percentage ?? null,
        is_contingent: false,
        is_hidden: false,
      });
      if (error) throw error;
    },
    onSuccess: (_, params) => {
      if (!isDemoMode) invalidate(params.estate_item_id);
    },
  });

  const updateBeneficiary = useMutation({
    mutationFn: async (params: UpdateBeneficiaryParams) => {
      if (isDemoMode) {
        queryClient.setQueryData<ItemBeneficiary[]>(
          ["item-beneficiaries", params.estate_item_id],
          (prev = []) =>
            prev.map((b) =>
              b.id === params.id ? { ...b, share_percentage: params.share_percentage ?? null } : b,
            ),
        );
        return;
      }
      const { error } = await supabase
        .from("estate_item_beneficiaries")
        .update({ share_percentage: params.share_percentage ?? null })
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: (_, params) => {
      if (!isDemoMode) invalidate(params.estate_item_id);
    },
  });

  const removeBeneficiary = useMutation({
    mutationFn: async (params: RemoveBeneficiaryParams) => {
      if (isDemoMode) {
        queryClient.setQueryData<ItemBeneficiary[]>(
          ["item-beneficiaries", params.estate_item_id],
          (prev = []) => prev.filter((b) => b.id !== params.id),
        );
        return;
      }
      const { error } = await supabase
        .from("estate_item_beneficiaries")
        .update({ is_hidden: true })
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: (_, params) => {
      if (!isDemoMode) invalidate(params.estate_item_id);
    },
  });

  return {
    addBeneficiary: addBeneficiary.mutate,
    addBeneficiaryAsync: addBeneficiary.mutateAsync,
    updateBeneficiary: updateBeneficiary.mutate,
    removeBeneficiary: removeBeneficiary.mutate,
    isAdding: addBeneficiary.isPending,
    isUpdating: updateBeneficiary.isPending,
    isRemoving: removeBeneficiary.isPending,
  };
}
